import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RootState } from 'store/reducers';
import { DisputeDetail, DisputeEvidence, DisputeListPreset, DisputeReason } from './disputes.model';
import { DISPUTE_RESPONSE, DISPUTE_STATUS } from './constants';
import { DISPUTE_TAB_TITLE } from './constants';

const initialDisputeDetail: DisputeDetail = {
  id: '',
  statusDetail: '',
  merchantStatus: '',
  reasonForDispute: '',
  messageForMerchant: '',
  transactionId: '',
  transactionAmount: '',
  customerName: '',
  collectedBy: '',
  invoiceNumber: '',
  paymentMethod: '',
  paymentMethodDetail: '',
  deductionStatus: '',
  deductionDetail: '',
  amount: '',
  disputeFee: '',
  respondBy: '',
  resultDate: '',
  transactionDate: '',
  collectedByProfileUrl: '',
  deductBy: '',
  deductedBy: '',
  internalStatus: '',
  isCBP: false,
  toolTipContent: '',
  invoiceId: '',
  deductionState: '',
  merchantComment: '',
  merchantReason: '',
  teamEmail: '',
  teamName: '',
  updatedAt: '',
  claimDocumentUrl: '',
  bankResponseDocumentId: '',
  issuingBankResponseUrl: '',
  closedAt: '',
  deductionOption: '',
  isPartialRecovery: false,
  merchantTimezone: '',
  recoveryAmount: '',
  teamId: '',
  claimDocumentId: '',
  formattedRespondBy: '',
  formattedTransactionDate: '',
  formattedUpdatedAt: ''
};

const initialState = {
  uploadedDocList: [],
  disputeDetail: initialDisputeDetail,
  disputeResponse: '',
  disputeReason: null,
  otherDisputeReason: '',
  supportingComments: '',
  disputeReasonsFilter: [],
  disputedStartDate: null,
  disputedEndDate: null,
  respondByStartDate: null,
  respondByEndDate: null,
  amount: { min: '', max: '' },
  statuses: [DISPUTE_STATUS.NEEDS_RESPONSE],
  currentTab: DISPUTE_TAB_TITLE.NEEDS_RESPONSE,
  previousRoute: '',
  merchantId: [] as string[],
  exactDisputeDates: false,
  exactDisputedStartDate: null as string | null,
  exactDisputedEndDate: null as string | null
};

export const disputesSlice = createSlice({
  name: 'disputes',
  initialState,
  reducers: {
    resetDisputeState: () => initialState,
    updateUploadedDocList: (state, { payload }: PayloadAction<DisputeEvidence[]>) => {
      state.uploadedDocList = payload;
    },
    updateDisputeDetail: (state, { payload }: PayloadAction<DisputeDetail>) => {
      state.disputeDetail = payload;
    },
    setDisputeResponse: (state, { payload }: PayloadAction<DISPUTE_RESPONSE>) => {
      state.disputeResponse = payload;
    },
    setDisputeReason: (state, { payload }: PayloadAction<DisputeReason | string>) => {
      state.disputeReason = payload;
    },
    setOtherDisputeReason: (state, { payload }: PayloadAction<string>) => {
      state.otherDisputeReason = payload;
    },
    setSupportingComments: (state, { payload }: PayloadAction<string>) => {
      state.supportingComments = payload;
    },
    setCurrentTab: (state, { payload }: PayloadAction<DISPUTE_TAB_TITLE>) => {
      state.currentTab = payload;
    },
    setDisputeReasonsFilter: (state, { payload }: PayloadAction<string[]>) => {
      state.disputeReasonsFilter = payload;
    },
    setDisputedStartDate: (state, { payload }: PayloadAction<string>) => {
      state.disputedStartDate = payload;
      state.exactDisputeDates = false;
    },
    setDisputedEndDate: (state, { payload }: PayloadAction<string>) => {
      state.disputedEndDate = payload;
      state.exactDisputeDates = false;
    },
    setRespondByStartDate: (state, { payload }: PayloadAction<string>) => {
      state.respondByStartDate = payload;
    },
    setRespondByEndDate: (state, { payload }: PayloadAction<string>) => {
      state.respondByEndDate = payload;
    },
    setDisputeStatuses: (state, { payload }: PayloadAction<DISPUTE_STATUS[]>) => {
      state.statuses = payload;
    },
    setAmountFilterValue: (state, { payload }: PayloadAction<{ min: string; max: string }>) => {
      state.amount = payload;
    },
    updatePreviousRoute: (state, { payload }: PayloadAction<string>) => {
      state.previousRoute = payload;
    },
    setDisputeMerchantId: (state, { payload }: PayloadAction<string[]>) => {
      state.merchantId = payload;
    },
    applyDisputeListPreset: (state, { payload }: PayloadAction<DisputeListPreset>) => {
      state.currentTab = payload.tab;
      state.statuses = payload.statuses;
      state.disputeReasonsFilter = payload.reasons;
      state.disputedStartDate = payload.startDisputedDate;
      state.disputedEndDate = payload.endDisputedDate;
      state.exactDisputeDates = payload.exactDisputeDates;
      state.exactDisputedStartDate = payload.exactDisputedStartDate;
      state.exactDisputedEndDate = payload.exactDisputedEndDate;
      if (Array.isArray(payload.merchantId)) {
        state.merchantId = payload.merchantId.filter(Boolean);
      } else if (payload.merchantId) {
        state.merchantId = [payload.merchantId];
      } else {
        state.merchantId = [];
      }
      state.amount = { min: '', max: '' };
      state.respondByStartDate = null;
      state.respondByEndDate = null;
    }
  }
});

export const {
  resetDisputeState,
  updateDisputeDetail,
  updateUploadedDocList,
  setDisputeResponse,
  setDisputeReason,
  setOtherDisputeReason,
  setSupportingComments,
  setCurrentTab,
  setDisputeReasonsFilter,
  setDisputedEndDate,
  setDisputedStartDate,
  setRespondByEndDate,
  setRespondByStartDate,
  setDisputeStatuses,
  setAmountFilterValue,
  updatePreviousRoute,
  setDisputeMerchantId,
  applyDisputeListPreset
} = disputesSlice.actions;

export const selectUploadedDocumentList = (state: RootState) => state.rootReducer.disputes.uploadedDocList;
export const selectDisputeDetail = (state: RootState) => state.rootReducer.disputes.disputeDetail;
export const selectDisputeResponse = (state: RootState) => state.rootReducer.disputes.disputeResponse;
export const selectDisputeReason = (state: RootState) => state.rootReducer.disputes.disputeReason;
export const selectOtherDisputeReason = (state: RootState) => state.rootReducer.disputes.otherDisputeReason;
export const selectSupportingComments = (state: RootState) => state.rootReducer.disputes.supportingComments;
export const selectCurrentTab = (state: RootState) => state.rootReducer.disputes.currentTab;
export const selectDisputeReasonsFilter = (state: RootState) => state.rootReducer.disputes.disputeReasonsFilter;
export const selectDisputedEndDate = (state: RootState) => state.rootReducer.disputes.disputedEndDate;
export const selectDisputedStartDate = (state: RootState) => state.rootReducer.disputes.disputedStartDate;
export const selectRespondByStartDate = (state: RootState) => state.rootReducer.disputes.respondByStartDate;
export const selectRespondByEndDate = (state: RootState) => state.rootReducer.disputes.respondByEndDate;
export const selectDisputeStatuses = (state: RootState) => state.rootReducer.disputes.statuses;
export const selectAmount = (state: RootState) => state.rootReducer.disputes.amount;
export const selectPreviousRoute = (state: RootState) => state.rootReducer.disputes.previousRoute;
export const selectDisputeMerchantId = (state: RootState) => state.rootReducer.disputes.merchantId;
export const selectExactDisputeDates = (state: RootState) => state.rootReducer.disputes.exactDisputeDates;
export const selectExactDisputedStartDate = (state: RootState) => state.rootReducer.disputes.exactDisputedStartDate;
export const selectExactDisputedEndDate = (state: RootState) => state.rootReducer.disputes.exactDisputedEndDate;

export default disputesSlice.reducer;
